import React from 'react';
import styled from 'styled-components';

import { Headline } from '@oneaudi/unified-web-components';
import {
  ThemeProvider,
  SYS_SPACE_RELATIVE_MD,
  SYS_SPACE_RELATIVE_LG,
  SYS_SPACE_RELATIVE_2_XL,
  SYS_SPACE_RELATIVE_3_XL,
  SYS_SPACE_RELATIVE_XL,
  SYS_PAGE_MARGIN,
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_XL,
  SYS_BREAKPOINT_2_XL,
} from '@oneaudi/unified-web-common';
import { renderTextWithFootnotesReferencesV2 } from '@oneaudi/feature-app-utils';
import { UeElement } from '@oneaudi/falcon-tools';

import { getThemeName } from '../../../alpha-helper';
import type { AppContent, TeaserHeadlineTag } from '../../types';
import { getBackgroundColor } from '../../utils/getBackgroundColor';
import { useTracking } from '../../utils/useTracking';
import { enableHTMLEntities } from '../../utils/enableHTMLEntities';

import { BasicTeaserLike } from './BasicTeaserLike';
import { TileTeaser } from './TileTeaser';

const StyledArticleTeaser = styled.div`
  background-color: ${(props) => getBackgroundColor(props.theme.name)};
  padding-top: ${process.env.IS_LEGACY ? SYS_SPACE_RELATIVE_3_XL : SYS_SPACE_RELATIVE_XL};
  padding-bottom: ${process.env.IS_LEGACY ? SYS_SPACE_RELATIVE_3_XL : SYS_SPACE_RELATIVE_XL};
  padding-left: ${process.env.IS_LEGACY ? SYS_PAGE_MARGIN : SYS_SPACE_RELATIVE_3_XL};
  padding-right: ${process.env.IS_LEGACY ? SYS_PAGE_MARGIN : SYS_SPACE_RELATIVE_3_XL};

  .audi-footnote-anchor__text {
    font-size: 0.8em;
    font-weight: lighter;
  }

  .top-headline > .audi-footnote-anchor__text {
    // footnote reference size fix
    vertical-align: super;
    font-size: 24px !important;
  }
`;

export interface StyledTeaserContainerProps {
  hasMarginTop?: boolean;
  maxColumns: number;
}

const StyledTeaserContainer = styled.div<StyledTeaserContainerProps>`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-auto-rows: auto;

  ${(props) => {
    return `
      grid-gap: ${process.env.IS_LEGACY ? SYS_SPACE_RELATIVE_LG : SYS_SPACE_RELATIVE_2_XL} ${SYS_SPACE_RELATIVE_MD};

      @media (min-width: ${SYS_BREAKPOINT_MD}) {
        grid-auto-rows: 1fr;
        grid-template-columns: repeat(${Math.min(2, props.maxColumns)}, 1fr);
      }


      @media (min-width: ${SYS_BREAKPOINT_XL}) {
        grid-template-columns: repeat(${Math.min(3, props.maxColumns)}, 1fr);
      }

      @media (min-width: ${SYS_BREAKPOINT_2_XL}) {
        grid-template-columns: repeat(${Math.min(4, props.maxColumns)}, 1fr);
      }
    `;
  }}
`;

const StyledHeadline = styled(Headline)`
  margin-block-end: ${SYS_SPACE_RELATIVE_XL};
`;

export const ArticleTeaser: React.FC<AppContent> = (props) => {
  const { headline, headlineTag, articleIntros, maxColumns, theme } = props;
  const singleTeaserMode = Boolean(articleIntros && articleIntros.length === 1);

  const ref = React.useRef(null);
  const tracking = useTracking(
    ref,
    headline,
    articleIntros && articleIntros.length > 0
      ? articleIntros.map((articleIntro) => ({
          elementName: 'article',
          elementValue: `${articleIntro.index + 1}-${articleIntro.headline}`,
        }))
      : [],
    {
      implementer: 37,
    },
  );

  React.useEffect(() => {
    tracking.sendReadyEvent();
    tracking.registerImpressionTracking();
    /* eslint-disable-next-line react-hooks/exhaustive-deps --
     * We skip the rule here because we just want to run the code once and not define a dependency
     * here. Omitting the entire dependency array or adding the `tracking` object as dependency
     * would run the code more than once, causing issues with registering impression tracking event.
     **/
  }, []);

  const teaserHeadlineTag =
    // SEO optimization: teaser headline tag is one hierarchy level lower than main component headline tag
    `h${parseInt(headlineTag.replace(/\D/g, ''), 10) + 1}` as TeaserHeadlineTag;

  return (
    <ThemeProvider theme={{ name: getThemeName(theme) }}>
      <StyledArticleTeaser ref={ref} data-test-id="article-teaser">
        {headline && (
          <StyledHeadline
            className="top-headline"
            tag={headlineTag}
            variant={{ type: 'headline', order: '1', weight: 'normal' }}
          >
            <UeElement type="text" property="headline" label="Headline">
              {renderTextWithFootnotesReferencesV2(enableHTMLEntities(headline))}
            </UeElement>
          </StyledHeadline>
        )}
        {!singleTeaserMode && (
          <StyledTeaserContainer maxColumns={maxColumns}>
            {articleIntros &&
              articleIntros.map((articleIntro, i) => (
                <TileTeaser
                  key={`item-${i}`}
                  articleIntro={articleIntro}
                  onTeaserClick={tracking.sendClickEvent}
                  teaserHeadlineTag={teaserHeadlineTag}
                />
              ))}
          </StyledTeaserContainer>
        )}
        {singleTeaserMode && (
          <BasicTeaserLike
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            articleIntro={articleIntros![0]}
            onTeaserClick={tracking.sendClickEvent}
            teaserHeadlineTag={teaserHeadlineTag}
          />
        )}
      </StyledArticleTeaser>
    </ThemeProvider>
  );
};
